import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Globe,
  MapPin,
  Clock,
  Banknote,
  Languages,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
} from "lucide-react";
import { WeatherWidget } from "../components/WeatherWidget";
import { POIDiscovery } from "../components/POIDiscovery";
import { NearbyEssentials } from "../components/NearbyEssentials";

import { getCountryPhotoByCode } from "../utils/countryPhotos";

const CountryInfo = () => {
  const { countryCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllTimezones, setShowAllTimezones] = useState(false);
  const [passportCountry, setPassportCountry] = useState(null);
  const [passportLoaded, setPassportLoaded] = useState(false);
  const [allPassports, setAllPassports] = useState([]);

  useEffect(() => {
    fetchPassportInfo();
  }, []);

  useEffect(() => {
    if (passportLoaded && user) {
      fetchCountryInfo();
    }
  }, [countryCode, user, passportCountry, passportLoaded]);

  const fetchPassportInfo = async () => {
    try {
      const response = await axios.get(`${API}/documents`, { withCredentials: true });
      const passports = response.data.filter(d => d.document_type === "passport" && d.issuing_country);
      setAllPassports(passports);
      if (passports.length > 0) {
        setPassportCountry(passports[0].issuing_country || null);
      }
    } catch (error) {
      // Ignore — will fall back to home_country
    } finally {
      setPassportLoaded(true);
    }
  };

  const fetchCountryInfo = async () => {
    try {
      const homeCountry = user?.home_country || "";
      const passport = passportCountry || "";
      const response = await axios.get(
        `${API}/countries/${countryCode}?home_country=${encodeURIComponent(homeCountry)}&passport_country=${encodeURIComponent(passport)}`,
        { withCredentials: true }
      );
      setCountry(response.data);
    } catch (error) {
      toast.error("Failed to load country information");
    } finally {
      setLoading(false);
    }
  };

  // Get LIVE current time in a destination using IANA zone name (always accurate, handles DST)
  const getLiveTime = (tz) => {
    try {
      return new Date().toLocaleTimeString("en-US", {
        timeZone: tz.zone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch {
      return tz.current_time || "--:--";
    }
  };

  // Get timezone difference between user's local time and destination
  const getTimezoneDiff = (tz) => {
    try {
      const now = new Date();
      // Get destination offset
      const destStr = now.toLocaleString("en-US", { timeZone: tz.zone, timeZoneName: "shortOffset" });
      const localStr = now.toLocaleString("en-US", { timeZoneName: "shortOffset" });
      const parseGMT = (s) => {
        const m = s.match(/GMT([+-]?\d+(?::\d+)?)/);
        if (!m) return 0;
        const parts = m[1].split(":");
        const h = parseInt(parts[0], 10);
        const min = parts[1] ? parseInt(parts[1], 10) * (h < 0 ? -1 : 1) : 0;
        return h + min / 60;
      };
      const diff = parseGMT(destStr) - parseGMT(localStr);
      if (Math.abs(diff) < 0.01) return { text: "Same as your time", color: "text-emerald-400" };
      const sign = diff > 0 ? "+" : "";
      const hours = Math.floor(Math.abs(diff));
      const minutes = Math.round((Math.abs(diff) - hours) * 60);
      let timeStr = `${sign}${diff > 0 ? "" : "-"}${hours}`;
      if (minutes > 0) timeStr += `:${minutes.toString().padStart(2, "0")}`;
      timeStr += " hrs";
      return {
        text: timeStr,
        color: Math.abs(diff) <= 3 ? "text-emerald-400" : Math.abs(diff) <= 6 ? "text-amber-400" : "text-orange-400",
      };
    } catch {
      return { text: `UTC ${tz.offset}`, color: "text-zinc-400" };
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  if (!country) {
    return (
      <div className="page-container flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl text-white mb-2">Country not found</h2>
          <Button onClick={() => navigate("/dashboard")} className="btn-primary">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const visaInfo = country.user_visa_info || country.visa_info?.default;

  return (
    <div className="page-container" data-testid="country-info">
      <SEO
        title={country?.name ? `${country.name} — Visa & Travel Info` : "Country Info"}
        description={country?.name ? `Visa requirements, weather forecast, points of interest, and essential travel information for ${country.name}.` : "Country travel information and visa requirements."}
        path={`/country/${countryCode}`}
      />
      {/* Hero Section with Flag */}
      <div
        className="relative h-48 md:h-64 bg-cover bg-center overflow-hidden"
      >
        <img
          src={getCountryPhotoByCode(countryCode)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/30 via-[#09090b]/50 to-[#09090b]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <img
              src={country.flag}
              alt={`${country.name} flag`}
              className="w-24 h-16 md:w-32 md:h-20 rounded-lg shadow-lg mx-auto mb-4 object-cover border border-white/20"
              data-testid="country-flag"
            />
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-white drop-shadow-lg">
              {country.name}
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Quick Facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-4 text-center">
            <MapPin className="w-6 h-6 text-sky-400 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Capital</p>
            <p className="text-white font-medium mt-1" data-testid="country-capital">
              {country.capital}
            </p>
          </div>
          <div className="glass-card p-4 text-center">
            <Clock className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Time Zone</p>
            {country.timezones && country.timezones.length > 0 ? (
              <div className="mt-1">
                <p className="text-white font-medium text-lg" data-testid="country-timezone">
                  {getLiveTime(country.timezones[0])}
                </p>
                <p className="text-xs text-zinc-400">{country.timezones[0].label}</p>
                <p className="text-xs text-zinc-500">UTC {country.timezones[0].offset}</p>
                {(() => {
                  const diff = getTimezoneDiff(country.timezones[0]);
                  return (
                    <p className={`text-xs ${diff.color} font-medium`}>
                      {diff.text}
                    </p>
                  );
                })()}
                {country.timezones.length > 1 && (
                  <button
                    onClick={() => setShowAllTimezones(!showAllTimezones)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 flex items-center justify-center gap-1 mx-auto"
                  >
                    +{country.timezones.length - 1} more
                    {showAllTimezones ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-white font-medium mt-1" data-testid="country-timezone">
                {country.utc_offset}
              </p>
            )}
          </div>
          <div className="glass-card p-4 text-center">
            <Banknote className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Currency</p>
            <p className="text-white font-medium mt-1">{country.currency}</p>
          </div>
          <div className="glass-card p-4 text-center">
            <Languages className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Language</p>
            <p className="text-white font-medium mt-1">{country.language}</p>
          </div>
        </div>

        {/* All Timezones Panel */}
        {showAllTimezones && country.timezones && country.timezones.length > 1 && (
          <div className="glass-card p-4" data-testid="all-timezones">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-400" />
              All Timezones ({country.timezones.length})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {country.timezones.map((tz, idx) => {
                const diff = getTimezoneDiff(tz);
                return (
                  <div 
                    key={idx}
                    className="p-2 bg-slate-800/50 rounded-lg border border-slate-700/50"
                  >
                    <p className="text-sm text-white font-medium">{getLiveTime(tz)}</p>
                    <p className="text-xs text-zinc-400">{tz.label}</p>
                    <p className="text-xs text-zinc-500">UTC {tz.offset}</p>
                    <p className={`text-xs ${diff.color}`}>{diff.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Visa Status */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-heading text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-sky-400" />
              Visa Information
            </h2>
            {allPassports.length > 1 && (
              <Select value={passportCountry || ""} onValueChange={setPassportCountry}>
                <SelectTrigger className="glass-input w-auto h-8 text-xs gap-1" data-testid="passport-selector">
                  <SelectValue placeholder="Select passport" />
                </SelectTrigger>
                <SelectContent className="glass-card border-white/10">
                  {allPassports.map((p) => (
                    <SelectItem key={p.document_id} value={p.issuing_country}>
                      {p.issuing_country} passport
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {(passportCountry || user?.home_country) && (
            <p className="text-xs text-zinc-500 mb-3" data-testid="passport-context">
              Showing visa requirements for <span className="text-zinc-300 font-medium">{passportCountry || user.home_country}</span> passport holders
            </p>
          )}

          <div className="flex items-center gap-4 mb-4">
            {visaInfo?.requirement === "visa_free" || visaInfo?.requirement === "home" ? (
              <div
                className="visa-badge visa-free"
                data-testid="visa-badge-free"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Visa Free
              </div>
            ) : visaInfo?.requirement === "visa_on_arrival" ? (
              <div
                className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium text-sm flex items-center"
                data-testid="visa-badge-voa"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Visa on Arrival
              </div>
            ) : visaInfo?.requirement === "eta" ? (
              <div
                className="px-3 py-1.5 rounded-full bg-sky-500/20 text-sky-400 font-medium text-sm flex items-center"
                data-testid="visa-badge-eta"
              >
                <Globe className="w-4 h-4 mr-2" />
                ETA Required
              </div>
            ) : visaInfo?.requirement === "e_visa" ? (
              <div
                className="px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 font-medium text-sm flex items-center"
                data-testid="visa-badge-evisa"
              >
                <Globe className="w-4 h-4 mr-2" />
                e-Visa Required
              </div>
            ) : visaInfo?.requirement === "no_admission" ? (
              <div
                className="px-3 py-1.5 rounded-full bg-red-600/20 text-red-400 font-medium text-sm flex items-center"
                data-testid="visa-badge-no-admission"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                No Admission
              </div>
            ) : (
              <div
                className="visa-badge visa-required"
                data-testid="visa-badge-required"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Visa Required
              </div>
            )}
            {visaInfo?.max_stay > 0 && (
              <span className="text-zinc-400 text-sm">
                Max stay: {visaInfo.max_stay} days
              </span>
            )}
          </div>

        </div>

        {/* Weather Forecast */}
        <WeatherWidget countryCode={countryCode} />

        {/* Discover Places */}
        <POIDiscovery countryCode={countryCode} />

        {/* Nearby Essentials */}
        <NearbyEssentials countryCode={countryCode} />

        {/* External Links */}
        <div className="grid grid-cols-2 gap-4">
          <a
            href={country.wikivoyage_url}
            target="_blank"
            rel="noopener noreferrer"
            className="glass-card p-4 hover:border-sky-500/30 transition-all flex items-center justify-center gap-2"
            data-testid="wikivoyage-link"
          >
            <span className="text-sky-400 font-medium">Wikivoyage</span>
            <ExternalLink className="w-4 h-4 text-zinc-500" />
          </a>
          <a
            href={country.wiki_url}
            target="_blank"
            rel="noopener noreferrer"
            className="glass-card p-4 hover:border-violet-500/30 transition-all flex items-center justify-center gap-2"
            data-testid="wikipedia-link"
          >
            <span className="text-violet-400 font-medium">Wikipedia</span>
            <ExternalLink className="w-4 h-4 text-zinc-500" />
          </a>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => navigate(`/route-sights/${countryCode}`)}
            className="flex-1 btn-primary"
            data-testid="view-sights-button"
          >
            View Route & Sights
          </Button>
          <Button
            onClick={() => navigate("/ai-planner")}
            variant="outline"
            className="flex-1 btn-outline"
            data-testid="plan-trip-button"
          >
            Plan Trip with AI
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CountryInfo;
